import { BillingPlan } from "@/hooks/useBilling";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Loader2 } from "lucide-react";
import { useState } from "react";

const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  business: "Business",
  enterprise: "Enterprise",
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  free: "Para começar a explorar",
  starter: "Para pequenas comunidades",
  pro: "Para comunidades em crescimento",
  business: "Para operações avançadas",
  enterprise: "Sob medida para você",
};

function formatPrice(cents: number) {
  if (cents === 0) return "Grátis";
  return `R$ ${(cents / 100).toFixed(0)}/mês`;
}

function formatLimit(value: number | null) {
  if (value === null) return "Ilimitado";
  return value.toLocaleString("pt-BR");
}

interface PricingCardsProps {
  plans: BillingPlan[];
  currentPlanName?: string | null;
  onSelectPlan: (planId: string) => Promise<void>;
  stripeConfigured?: boolean;
}

export function PricingCards({ plans, currentPlanName, onSelectPlan, stripeConfigured = false }: PricingCardsProps) {
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const handleSelect = async (planId: string) => {
    setLoadingPlanId(planId);
    try {
      await onSelectPlan(planId);
    } catch {
      setLoadingPlanId(null);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      {plans.map((plan) => {
        const isCurrent = plan.name === currentPlanName;
        const isRecommended = plan.name === "pro";
        const isEnterprise = plan.name === "enterprise";

        return (
          <Card
            key={plan.id}
            className={`relative flex flex-col ${isRecommended ? "border-primary shadow-lg ring-2 ring-primary/20" : ""}`}
          >
            {isRecommended && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground flex items-center gap-1">
                  <Star className="w-3 h-3" /> Recomendado
                </Badge>
              </div>
            )}
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{PLAN_LABELS[plan.name] || plan.name}</CardTitle>
              <p className="text-xs text-muted-foreground">{PLAN_DESCRIPTIONS[plan.name]}</p>
              <p className="text-2xl font-bold mt-2">{formatPrice(plan.price_brl)}</p>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-between gap-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-success shrink-0" />
                  <span>{formatLimit(plan.monthly_message_limit)} msgs/mês</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-success shrink-0" />
                  <span>{formatLimit(plan.monthly_ai_calls_limit)} chamadas IA</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-success shrink-0" />
                  <span>{formatLimit(plan.max_groups)} grupos</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-success shrink-0" />
                  <span>{formatLimit(plan.max_members)} membros</span>
                </li>
              </ul>
              {isCurrent ? (
                <Button variant="outline" disabled className="w-full">Plano Atual</Button>
              ) : isEnterprise ? (
                <Button variant="outline" className="w-full" onClick={() => window.open("mailto:contato@syra.app?subject=Enterprise", "_blank")}>
                  Fale Conosco
                </Button>
              ) : (
                <Button
                  className="w-full"
                  variant={isRecommended ? "default" : "outline"}
                  disabled={!stripeConfigured || loadingPlanId === plan.id || !plan.stripe_price_id}
                  onClick={() => handleSelect(plan.id)}
                >
                  {loadingPlanId === plan.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {!stripeConfigured ? "Stripe não configurado" : !plan.stripe_price_id ? "Price ID pendente" : "Assinar"}
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
